import { ReactNode, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Input, Checkbox, Select, SearchableSelect, IconPencil } from '../atoms';
import { useToast } from './Toast';
import { LastUpdated } from './LastUpdated';
import { MultiSelectChips } from './MultiSelectChips';
import { STATE_SELECT_OPTIONS } from '../lib/us-states';
import { formatPhone } from '../lib/formatPhone';
import { usePermissions } from '../lib/usePermissions';

/**
 * InlineEditCard — an overview box you edit IN PLACE (punchlist §1).
 *
 * Replaces the "one Edit button → whole-record popup" pattern: each box (Identity,
 * Contract, Reference, …) carries its own Edit control and saves just its own
 * fields via PUT /api/<routePath>/:id. The bottom-right shows a per-SECTION
 * "last updated by · when" (§1.2) sourced from field-level audit attribution, so
 * editing one box never restamps the others.
 */

export type InlineFieldType = 'text' | 'number' | 'textarea' | 'date' | 'url' | 'checkbox' | 'select' | 'searchable-select' | 'multi-select' | 'state' | 'postal' | 'phone';

export interface InlineEditField {
  key: string;
  label: string;
  type?: InlineFieldType; // default 'text'
  /** Options for a `select` / `searchable-select` / `multi-select` field. For a plain
   *  select the current value is kept selectable even if it's not in the list (so an
   *  existing free-text value is never lost). A `searchable-select` renders a typeahead
   *  combobox (SearchableSelect) — use it for long FK pickers (parcels, clients). A
   *  `multi-select` holds an ARRAY of values, edited as chips with an add menu, and PUTs
   *  the array (spec 0011: a client's categories). */
  options?: { value: string; label: string }[]
    /** A FUNCTION when the choices depend on another field being edited in the SAME
     *  card — it receives the live draft, so an Event list can narrow to the Forum
     *  currently selected rather than to the one last saved. Without this the two
     *  selects can only disagree: changing Forum would leave Event offering the old
     *  forum's events, which is precisely how a value ends up tagged
     *  `ARB · petition filed`. */
    | ((draft: Record<string, unknown>) => { value: string; label: string }[]);
  /** Keep a select's options in the order given (a stage ladder, a status progression)
   *  instead of alphabetical. */
  preserveOrder?: boolean;
  /** Helper note shown under the field in edit mode — e.g. "add more in Org Settings". */
  hint?: ReactNode;
  /** Org-settings registry slug for a select field. When set, an inline "+ Add an
   *  option" affordance (retest C2) lets a super admin / office administrator add a
   *  missing option without leaving the flow; the new value is selected on save. */
  addOptionSlug?: string;
  /** Required (NOT NULL) — save is blocked if emptied, and a * marker is shown. */
  required?: boolean;
  /** Read-only inside an otherwise-editable card (immutable / system columns):
   *  shown in both views but never rendered as an input, and omitted from the PUT
   *  body. A card whose fields are ALL read-only shows no Edit affordance. */
  readOnly?: boolean;
  /** Read-view renderer for the current value (default: string, or "—" when empty). */
  display?: (value: unknown) => ReactNode;
}

export interface InlineEditCardProps {
  title: string;
  fields: InlineEditField[];
  record: Record<string, unknown>;
  /** API slug mounted at /api/<routePath>/:id, e.g. 'clients'. */
  routePath: string;
  recordId: string;
  /** Query keys to invalidate on save (the record view + its field-attribution). */
  invalidateKeys?: unknown[][];
  /** Per-section attribution: the latest change to THIS box's fields. */
  attribution?: { by: string | null; at: string | null } | null;
  canEdit?: boolean;
}

const asInput = (v: unknown): string => (v == null ? '' : String(v));

/**
 * AddOptionInline (retest C2) — the universal "don't see it? add it" affordance for
 * a taxonomy dropdown. A super admin / office administrator adds the missing option
 * inline (POST /api/org-settings/:slug/add-option); it's appended to the registry,
 * the cached options refetch, and the new value is selected — all without leaving
 * the flow. Everyone else sees a note pointing to Org Settings.
 */
export function AddOptionInline({ slug, onAdded }: { slug: string; onAdded: (value: string) => void }) {
  const { data } = usePermissions();
  const role = data?.user.role;
  const canAdd = role === 'super_admin' || role === 'administrator';
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const mut = useMutation({
    mutationFn: (v: string) => api.post<{ value: string }>(`/api/org-settings/${slug}/add-option`, { value: v }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['org-settings', slug] });
      const added = (res && res.value) || value.trim();
      onAdded(added);
      setValue(''); setOpen(false);
      toast({ variant: 'success', message: `Added “${added}”.` });
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not add the option.' }),
  });

  if (!canAdd) {
    return <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Don’t see it? An admin can add it in Org Settings.</span>;
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)', fontFamily: 'inherit' }}
      >+ Add an option</button>
    );
  }
  const submit = () => { if (value.trim()) mut.mutate(value.trim()); };
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
      <Input
        value={value}
        autoFocus
        aria-label="New option"
        placeholder="New option"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } }}
      />
      <Button variant="primary" size="sm" loading={mut.isPending} disabled={!value.trim()} onClick={submit}>Add</Button>
      <Button variant="secondary" size="sm" onClick={() => setOpen(false)} disabled={mut.isPending}>Cancel</Button>
    </span>
  );
}

export function InlineEditCard({
  title, fields, record, routePath, recordId, invalidateKeys = [], attribution, canEdit = true,
}: InlineEditCardProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  // Seed the draft from the record each time we ENTER edit mode.
  useEffect(() => {
    if (!editing) return;
    const seed: Record<string, unknown> = {};
    for (const f of fields) {
      seed[f.key] = f.type === 'multi-select'
        ? (Array.isArray(record[f.key]) ? [...(record[f.key] as unknown[])] : [])
        : record[f.key] ?? (f.type === 'checkbox' ? false : '');
    }
    setDraft(seed);
    // fields/record are stable for a given card render; re-seed only on edit toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      for (const f of fields) {
        if (f.readOnly) continue; // immutable/system column — never PUT it
        const v = draft[f.key];
        if (f.type === 'checkbox') body[f.key] = !!v;
        else if (f.type === 'multi-select') body[f.key] = Array.isArray(v) ? v : [];
        else if (f.type === 'number') body[f.key] = v === '' || v == null ? null : Number(v);
        else body[f.key] = v === '' || v == null ? null : v;
      }
      return api.put(`/api/${routePath}/${recordId}`, body);
    },
    onSuccess: () => {
      for (const k of invalidateKeys) qc.invalidateQueries({ queryKey: k });
      toast({ variant: 'success', message: `${title} updated.` });
      setEditing(false);
    },
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError ? err.message : `Could not update ${title}.`,
    }),
  });

  return (
    <div style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{
          margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
        }}>{title}</h3>
        {canEdit && !editing && fields.some((f) => !f.readOnly) && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${title}`}
            title={`Edit ${title}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
              cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-xs)',
              fontWeight: 'var(--fw-medium)', fontFamily: 'inherit', padding: 0,
            }}
          >
            <IconPencil size={13} /> Edit
          </button>
        )}
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {fields.map((f) => {
            const cur = asInput(draft[f.key]);
            const declared = typeof f.options === 'function' ? f.options(draft) : (f.options ?? []);
            // Keep the current value selectable even if the org removed it from the registry.
            const opts = (f.type === 'select' || f.type === 'searchable-select')
              ? (cur && !declared.some((o) => o.value === cur) ? [{ value: cur, label: cur }, ...declared] : declared)
              : [];
            return (
              <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(f.type !== 'checkbox' || f.readOnly) && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{f.label}{f.required && !f.readOnly && <span style={{ color: 'var(--color-status-red)' }}> *</span>}</span>}
                {f.readOnly ? (
                  <span style={{ fontSize: 'var(--fs-md)', color: 'var(--color-ink-3)', padding: 'var(--sp-1) 0' }}>
                    {f.display ? f.display(record[f.key]) : (asInput(record[f.key]) || '—')}
                  </span>
                ) : f.type === 'checkbox' ? (
                  <Checkbox
                    label={f.label}
                    checked={!!draft[f.key]}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.checked }))}
                  />
                ) : f.type === 'select' ? (
                  <Select
                    options={opts}
                    preserveOrder={f.preserveOrder}
                    value={cur}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ) : f.type === 'multi-select' ? (
                  <MultiSelectChips
                    label={f.label}
                    options={declared.filter((o) => o.value !== '')}
                    value={Array.isArray(draft[f.key]) ? (draft[f.key] as string[]) : []}
                    onChange={(next) => setDraft((d) => ({ ...d, [f.key]: next }))}
                  />
                ) : f.type === 'searchable-select' ? (
                  <SearchableSelect
                    options={opts}
                    preserveOrder={f.preserveOrder}
                    value={cur || null}
                    onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                    placeholder="Search…"
                  />
                ) : f.type === 'textarea' ? (
                  <textarea
                    value={cur}
                    rows={4}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                    style={{
                      width: '100%', padding: 'var(--sp-2) var(--sp-3)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--r-md)', background: 'var(--color-card)', color: 'var(--color-ink)',
                      fontSize: 'var(--fs-md)', fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                    }}
                  />
                ) : f.type === 'state' ? (
                  // D — state is always a dropdown, never free text.
                  <Select
                    options={[{ value: '', label: '— Select —' }, ...STATE_SELECT_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))]}
                    value={cur}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ) : f.type === 'postal' ? (
                  // D — postal code: five digits, numbers only.
                  <Input
                    value={cur}
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="12345"
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                  />
                ) : f.type === 'phone' ? (
                  // D — phone: standard US format, numbers only.
                  <Input
                    value={cur}
                    inputMode="tel"
                    placeholder="XXX-XXX-XXXX"
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: formatPhone(e.target.value) }))}
                  />
                ) : (
                  <Input
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'url' ? 'url' : 'text'}
                    value={cur}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                )}
                {f.hint && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{f.hint}</span>}
                {f.addOptionSlug && !f.readOnly && (f.type === 'select' || f.type === 'searchable-select') && (
                  <AddOptionInline slug={f.addOptionSlug} onAdded={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} />
                )}
                {f.addOptionSlug && !f.readOnly && f.type === 'multi-select' && (
                  <AddOptionInline
                    slug={f.addOptionSlug}
                    onAdded={(v) => setDraft((d) => {
                      const cur = Array.isArray(d[f.key]) ? (d[f.key] as string[]) : [];
                      return { ...d, [f.key]: cur.includes(v) ? cur : [...cur, v] };
                    })}
                  />
                )}
              </label>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              loading={save.isPending}
              disabled={fields.some((f) => !f.readOnly && f.required && !String(draft[f.key] ?? '').trim())}
              onClick={() => save.mutate()}
            >Save</Button>
          </div>
        </div>
      ) : (
        <div>
          {fields.map((f) => {
            const raw = record[f.key];
            const content = f.display
              ? f.display(raw)
              : f.type === 'checkbox'
                ? (raw ? 'Yes' : 'No')
                : f.type === 'multi-select'
                  ? (Array.isArray(raw) && raw.length ? raw.join(', ') : '—')
                  : (raw != null && raw !== '' ? String(raw) : '—');
            return (
              <div key={f.key} style={{
                display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)',
                padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)',
              }}>
                <span style={{ color: 'var(--color-ink-3)', flexShrink: 0 }}>{f.label}</span>
                <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>{content}</span>
              </div>
            );
          })}
        </div>
      )}

      {!editing && attribution && (attribution.by || attribution.at) && (
        <div style={{ marginTop: 'auto', paddingTop: 'var(--sp-2)', textAlign: 'right' }}>
          <LastUpdated updatedBy={attribution.by} updatedAt={attribution.at} />
        </div>
      )}
    </div>
  );
}
